import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCommandDashboardData } from "./useCommandDashboardData";
import { formatActivityWhen } from "./activityDate";
import { CashFlowChart, CHART_COLORS, Donut, DayBars, Funnel, FUNNEL_COLORS } from "./commandCharts";
import {
  buildCashFlow,
  buildLabourWeek,
  buildPlanningDays,
  buildProjectHealth,
  buildTaskOverview,
  countProjectsWithOverdueTasks,
  formatMoneyCompact,
} from "../../../helpers/dashboard/commandDashboardModel";
import {
  hideHomeWidget,
  HOME_WIDGETS,
  insertionIndex,
  moveHomeWidget,
  placeHomeWidget,
  previewHomeOrder,
  resolveHomeDashboardLayout,
  showHomeWidget,
} from "../../../helpers/dashboard/homeDashboardLayout";
import {
  clearHomeDashboardPreference,
  loadHomeDashboardPreference,
  saveHomeDashboardPreference,
} from "../../../helpers/dashboard/homeDashboardPreference";
import { dayIndex, dayIndexToISO } from "../../../helpers/planning/planningModel";
import { CmdIcon } from "../../shared/CmdIcon";
import "./commandDashboard.css";

function Widget({ icon, title, subtitle, exit, aside, arrange, className = "", tone = "gold", children, testId, arrangeId, dragging, grabbed }) {
  return (
    <section
      className={`cmd-widget cmd-tone--${tone} ${className}${grabbed ? " is-grabbed" : ""}${dragging ? " is-dragging" : ""}`.trim()}
      data-testid={testId}
      data-arrange-id={arrangeId || undefined}
      data-grabbed={grabbed ? "true" : undefined}
    >
      {arrange || null}
      <header className="cmd-widget__head">
        <span className="cmd-widget__icon"><CmdIcon name={icon} /></span>
        <div className="cmd-widget__titles">
          <h2 className="cmd-widget__title">{title}</h2>
          {subtitle ? <p className="cmd-widget__sub">{subtitle}</p> : null}
        </div>
        {aside ? <div className="cmd-widget__aside">{aside}</div> : null}
        {exit ? (
          <button type="button" className="cmd-link" onClick={exit.onClick} disabled={!exit.onClick}>
            {exit.label} <CmdIcon name="arrow" className="cmd-link__arrow" />
          </button>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function Segmented({ options, value, onChange, label }) {
  return (
    <div className="cmd-seg" role="group" aria-label={label}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`cmd-seg__btn${value === opt.value ? " is-on" : ""}`}
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function Unavailable({ children }) {
  return <p className="cmd-empty">{children}</p>;
}

function stateNote(module, label) {
  if (module.status === "loading") return "Loading…";
  if (module.status === "error") return `${label} couldn’t be loaded right now.`;
  return `${label} isn’t available for this account.`;
}

const num = (module, pick) => (module.status === "ready" ? pick(module.data) : "—");

function AlertTile({ tone, icon, value, label, link, onClick, title }) {
  const body = (
    <>
      <span className="cmd-alert__icon"><CmdIcon name={icon} /></span>
      <span className="cmd-alert__value">{value}</span>
      <span className="cmd-alert__label">{label}</span>
      <span className="cmd-alert__link">{link} <CmdIcon name="arrow" className="cmd-link__arrow" /></span>
    </>
  );
  if (typeof onClick !== "function") {
    return <div className={`cmd-alert cmd-alert--${tone}`} title={title}>{body}</div>;
  }
  return (
    <button type="button" className={`cmd-alert cmd-alert--${tone}`} title={title} onClick={onClick}>
      {body}
    </button>
  );
}

function GlanceTile({ tone, icon, value, label, link, onClick }) {
  const body = (
    <>
      <span className="cmd-glance__icon"><CmdIcon name={icon} /></span>
      <span className="cmd-glance__text">
        <span className="cmd-glance__value">{value}</span>
        <span className="cmd-glance__label">{label}</span>
      </span>
      <span className="cmd-glance__link">{link} <CmdIcon name="arrow" className="cmd-link__arrow" /></span>
    </>
  );
  if (typeof onClick !== "function") {
    return <div className={`cmd-glance cmd-glance--${tone}`}>{body}</div>;
  }
  return (
    <button type="button" className={`cmd-glance cmd-glance--${tone}`} onClick={onClick}>
      {body}
    </button>
  );
}

function greetingFor(date) {
  const h = date.getHours();
  if (h < 12) return { text: "Good morning", icon: "sun" };
  if (h < 18) return { text: "Good afternoon", icon: "sun" };
  return { text: "Good evening", icon: "moon" };
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`.toUpperCase();
}

function relativeDay(iso, todayIso) {
  const a = dayIndex(iso);
  const b = dayIndex(todayIso);
  if (a == null || b == null) return "";
  const diff = b - a;
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff > 1 && diff < 7) return `${diff} days ago`;
  return iso;
}

/**
 * Purpose-built desktop dashboard (≥1024px) following concept CP-DSK-DSH-CMD-v01.
 * Presentation + read-only data: every number comes from existing data via
 * `useCommandDashboardData`; modules the viewer may not see are omitted, and
 * modules without a truthful source show an explicit unavailable state.
 */
export function DesktopCommandDashboard({
  variant = "desktop",
  currentUser,
  userName,
  activeSession,
  activeCrew = [],
  showActiveCrew,
  recentLogs = [],
  quickActions = [],
  onOpenAshleyHome,
  onOpenUserPreferences,
  onOpenGuide,
  onOpenSession,
  onOpenCrew,
  onOpenPlanning,
  onOpenProjects,
  onOpenProjectsWithOverdueTasks,
  onOpenProjectRow,
  onOpenEstimateHubWithStatus,
  onOpenAccountsReceivable,
  onOpenOverdueInvoices,
  onOpenAccountsPayable,
  onOpenFinance,
  onOpenTimeLogs,
  onOpenTimeLog,
  onOpenTimeLogsThisWeek,
  onArrangingChange,
}) {
  const data = useCommandDashboardData({ actor: currentUser });
  const { today, week, allow } = data;
  const [now] = useState(() => new Date());
  const [range, setRange] = useState("90D");
  const [labourWeek, setLabourWeek] = useState(0);
  const [arranging, setArranging] = useState(false);
  const [layoutRevision, setLayoutRevision] = useState(0);
  const [drag, setDrag] = useState(null);
  const [grabbedId, setGrabbedId] = useState(null);
  const gridRef = useRef(null);
  const dragCleanup = useRef(null);
  const layoutRef = useRef(null);
  const commitRef = useRef(null);

  useEffect(() => {
    onArrangingChange?.(arranging);
    return () => onArrangingChange?.(false);
  }, [arranging, onArrangingChange]);

  useEffect(() => () => dragCleanup.current?.(), []);

  const taskModel = useMemo(() => {
    if (data.tasks.status !== "ready") return null;
    const { tasks, projects } = data.tasks.data;
    return {
      overview: buildTaskOverview({ tasks, projects, today }),
      health: buildProjectHealth({ tasks, projects, today }),
      planning: buildPlanningDays({ tasks, projects, today }),
      overdueProjects: countProjectsWithOverdueTasks({ tasks, projects, today }),
    };
  }, [data.tasks, today]);

  const cashFlow = useMemo(
    () =>
      data.finance.status === "ready"
        ? buildCashFlow({ accounts: data.finance.data.accounts, movements: data.finance.data.movements, range, today })
        : null,
    [data.finance, range, today],
  );

  const labourThis = useMemo(
    () => (data.timeLogs.status === "ready" ? buildLabourWeek({ logs: data.timeLogs.data, weekStartIso: week.weekStartStr, offsetWeeks: labourWeek }) : null),
    [data.timeLogs, week.weekStartStr, labourWeek],
  );
  const hoursToday = useMemo(() => {
    if (data.timeLogs.status !== "ready") return null;
    return Math.round(
      data.timeLogs.data.filter((l) => l.date_iso === today).reduce((s, l) => s + (Number(l.total_hours) || 0), 0) * 10,
    ) / 10;
  }, [data.timeLogs, today]);

  const greeting = greetingFor(now);
  const firstName = (userName || "").split(" ")[0] || userName || "";
  const dateLabel = now.toLocaleDateString("en-CA", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
  const onBreak = activeCrew.filter((c) => c.break_active).length;
  const working = activeCrew.length - onBreak;

  // ── Needs Attention (unchanged section; the user may move or hide it) ──
  const attention = [];
  if (allow.tasks) {
    attention.push(
      <AlertTile key="overdue" tone="red" icon="alert" value={num(data.tasks, () => taskModel?.overview.overdue ?? 0)} label="Overdue Tasks" link="View Tasks" onClick={onOpenPlanning} />,
    );
    attention.push(
      <AlertTile
        key="overdue-projects"
        tone="orange"
        icon="folder"
        value={num(data.tasks, () => taskModel?.overdueProjects ?? 0)}
        label={<>Projects With<br />Overdue Tasks</>}
        title="Projects with at least one overdue task"
        link="View Projects"
        onClick={onOpenProjectsWithOverdueTasks}
      />,
    );
  }
  if (allow.receivables) {
    attention.push(
      <AlertTile key="inv" tone="gold" icon="doc" value={num(data.receivables, (d) => d.overdueCount)} label="Invoices Overdue" link="View Invoices" onClick={onOpenOverdueInvoices} />,
    );
  }
  if (allow.estimates) {
    attention.push(
      <AlertTile
        key="est"
        tone="blue"
        icon="doc"
        value={num(data.estimates, (d) => d.stages.find((s) => s.key === "sent")?.count ?? 0)}
        label="Estimates Waiting"
        title="Estimates sent and not yet approved"
        link="View Estimates"
        onClick={() => onOpenEstimateHubWithStatus?.("sent")}
      />,
    );
  }

  // ── Today at a Glance ─────────────────────────────────────────
  const glance = [];
  if (allow.tasks) {
    glance.push(<GlanceTile key="due" tone="green" icon="check" value={num(data.tasks, () => taskModel?.overview.today ?? 0)} label="Tasks Due" link="View Tasks" onClick={onOpenPlanning} />);
  }
  if (showActiveCrew) {
    glance.push(<GlanceTile key="crew" tone="blue" icon="users" value={working} label="Crew Working" link="View Team" onClick={() => onOpenCrew?.("all")} />);
  }
  glance.push(
    <GlanceTile key="hrs" tone="purple" icon="clock" value={hoursToday == null ? "—" : hoursToday.toFixed(1)} label="Hours Logged" link="View Time Logs" onClick={onOpenTimeLogs} />,
  );
  if (showActiveCrew) {
    glance.push(<GlanceTile key="break" tone="grey" icon="user" value={onBreak} label="On Break" link="View Team" onClick={() => onOpenCrew?.("on_break")} />);
  } else if (labourThis) {
    const thisWeek = buildLabourWeek({ logs: data.timeLogs.data, weekStartIso: week.weekStartStr });
    glance.push(<GlanceTile key="wk" tone="grey" icon="bars" value={thisWeek.total.toFixed(1)} label="Hours This Week" link="View Time Logs" onClick={onOpenTimeLogsThisWeek} />);
  }

  const taskDonut = taskModel?.overview;
  const donutSegments = taskDonut
    ? [
        { key: "overdue", label: "Overdue", value: taskDonut.overdue, color: CHART_COLORS.red },
        { key: "today", label: "Today", value: taskDonut.today, color: CHART_COLORS.gold },
        { key: "upcoming", label: "Upcoming", value: taskDonut.upcoming, color: CHART_COLORS.blue },
        { key: "completed", label: "Completed", value: taskDonut.completed, color: CHART_COLORS.green },
        ...(taskDonut.unscheduled ? [{ key: "unscheduled", label: "No due date", value: taskDonut.unscheduled, color: CHART_COLORS.grey }] : []),
      ]
    : [];

  const pipeline = data.estimates.status === "ready" ? data.estimates.data : null;
  const position = data.finance.status === "ready" ? data.finance.data.position : null;
  const apMetrics = data.payables.status === "ready" ? data.payables.data : null;
  const apValue = (key) => apMetrics?.find((m) => m.key === key)?.value;

  const financeTiles = [];
  if (allow.finance) {
    financeTiles.push({ key: "cash", tone: "green", icon: "wallet", value: position ? formatMoneyCompact(position.availableCash) : "—", label: "Available Cash", sub: position ? `Current ${formatMoneyCompact(position.currentCash)}` : stateNote(data.finance, "Cash") });
  }
  if (allow.receivables) {
    financeTiles.push({ key: "ar", tone: "blue", icon: "doc", value: data.receivables.status === "ready" ? formatMoneyCompact(data.receivables.data.outstanding) : "—", label: "Accounts Receivable", sub: data.receivables.status === "ready" ? `${data.receivables.data.overdueCount} overdue` : "" });
  }
  if (allow.payables) {
    financeTiles.push({ key: "ap", tone: "orange", icon: "wallet", value: apValue("liability") ?? "—", label: "Payroll Liability", sub: apValue("unpaid") != null ? `${apValue("unpaid")} unpaid entries` : "" });
  }
  if (allow.finance) {
    const net = position ? position.moneyInToday - position.moneyOutToday : null;
    financeTiles.push({ key: "net", tone: "purple", icon: "pulse", value: net == null ? "—" : formatMoneyCompact(net), label: "Net Change Today", sub: position ? `In ${formatMoneyCompact(position.moneyInToday)} · Out ${formatMoneyCompact(position.moneyOutToday)}` : "" });
  }

  const projectRows = taskModel?.health.rows.slice(0, 5) || [];
  const planning = taskModel?.planning;

  const needsNode = attention.length > 0 ? (
    <Widget className="cmd-span-7 cmd-wide-m" tone="red" icon="alert" title="Needs Attention" subtitle="Items that require your focus" testId="cmd-needs-attention">
      <div className="cmd-tiles cmd-tiles--alerts">{attention}</div>
    </Widget>
  ) : null;

  const todayNode = (
  <Widget className="cmd-span-5 cmd-wide-m" tone="green" icon="calendar" title="Today at a Glance" exit={{ label: "View Calendar", onClick: allow.tasks ? onOpenPlanning : undefined }} testId="cmd-today">
    <div className="cmd-tiles cmd-tiles--glance">{glance}</div>
  </Widget>
);

  const cashNode = allow.finance ? (
    <Widget
      className="cmd-span-7 cmd-wide-m"
      tone="gold"
      icon="dollar"
      title="Revenue & Cash Flow"
      subtitle={`Last ${range === "30D" ? "30 days" : range === "90D" ? "90 days" : "year"}`}
      aside={<Segmented label="Cash flow range" options={[{ value: "30D", label: "30D" }, { value: "90D", label: "90D" }, { value: "1Y", label: "1Y" }]} value={range} onChange={setRange} />}
      testId="cmd-cashflow"
    >
      {cashFlow ? (
        <>
          <ul className="cmd-legend">
            <li><i style={{ background: CHART_COLORS.in }} />Money In <b>{formatMoneyCompact(cashFlow.totals.in)}</b></li>
            <li><i style={{ background: CHART_COLORS.out }} />Money Out <b>{formatMoneyCompact(cashFlow.totals.out)}</b></li>
            <li><i style={{ background: CHART_COLORS.net }} />Net <b>{formatMoneyCompact(cashFlow.totals.net)}</b></li>
          </ul>
          <div className="cmd-chartwrap">
            <CashFlowChart buckets={cashFlow.buckets} ariaLabel={`Money in ${formatMoneyCompact(cashFlow.totals.in)}, money out ${formatMoneyCompact(cashFlow.totals.out)}, net ${formatMoneyCompact(cashFlow.totals.net)}`} />
            {!cashFlow.hasActivity ? <div className="cmd-chart-empty">No money movement recorded in this period.</div> : null}
          </div>
        </>
      ) : (
        <Unavailable>{stateNote(data.finance, "Cash flow")}</Unavailable>
      )}
    </Widget>
  ) : null;

  const healthNode = allow.tasks ? (
    <Widget
      className="cmd-span-5 cmd-wide-m"
      tone="green"
      icon="folder"
      title="Project Health"
      subtitle={taskModel ? `Active Projects (${taskModel.health.rows.length})` : "Active projects"}
      aside={
        taskModel ? (
          <div className="cmd-chips">
            <span className="cmd-chip cmd-chip--green">On Track {taskModel.health.counts.onTrack}</span>
            <span className="cmd-chip cmd-chip--red">Overdue {taskModel.health.counts.overdue}</span>
          </div>
        ) : null
      }
      exit={{ label: "View All", onClick: onOpenProjects }}
      testId="cmd-project-health"
    >
      {taskModel ? (
        projectRows.length ? (
          <ul className="cmd-projects">
            {projectRows.map((row) => (
              <li key={row.projectId}>
                <button type="button" className="cmd-project" onClick={() => onOpenProjectRow?.({ project_id: row.projectId, name: row.name })}>
                  <span className="cmd-project__tile">{initials(row.name)}</span>
                  <span className="cmd-project__name">{row.name}</span>
                  <span className={`cmd-bar cmd-bar--${row.health}`}>
                    <span style={{ width: `${row.percent ?? 0}%` }} />
                  </span>
                  <span className="cmd-project__pct">{row.percent == null ? "—" : `${row.percent}%`}</span>
                  <span className={`cmd-project__meta${row.overdue ? " is-red" : ""}`}>
                    {row.total === 0 ? "No tasks yet" : row.overdue ? `${row.overdue} overdue` : `${row.open} open`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <Unavailable>No active projects yet.</Unavailable>
        )
      ) : (
        <Unavailable>{stateNote(data.tasks, "Project health")}</Unavailable>
      )}
    </Widget>
  ) : null;

  const taskovNode = allow.tasks ? (
    <Widget className="cmd-span-4" tone="blue" icon="check" title="Task Overview" exit={{ label: "View All", onClick: onOpenPlanning }} testId="cmd-task-overview">
      {taskDonut ? (
        <div className="cmd-donutrow">
          <Donut segments={donutSegments} total={taskDonut.total} centerLabel="Total Tasks" ariaLabel={`${taskDonut.total} tasks: ${taskDonut.overdue} overdue, ${taskDonut.today} today, ${taskDonut.upcoming} upcoming, ${taskDonut.completed} completed`} />
          <ul className="cmd-donutlegend">
            {donutSegments.map((seg) => (
              <li key={seg.key}><i style={{ background: seg.color }} /><b>{seg.value}</b> {seg.label}</li>
            ))}
          </ul>
        </div>
      ) : (
        <Unavailable>{stateNote(data.tasks, "Tasks")}</Unavailable>
      )}
    </Widget>
  ) : null;

  const labourNode = allow.timeLogs ? (
    <Widget
      className="cmd-span-4"
      tone="blue"
      icon="bars"
      title="Labour Hours"
      subtitle={labourWeek === 0 ? "This week" : "Last week"}
      aside={<Segmented label="Labour week" options={[{ value: 0, label: "This Week" }, { value: -1, label: "Last Week" }]} value={labourWeek} onChange={setLabourWeek} />}
      testId="cmd-labour"
    >
      {labourThis ? (
        <>
          <div className="cmd-headline">
            <b>{labourThis.total.toFixed(1)}</b>
            <span>Hours {labourWeek === 0 ? "This Week" : "Last Week"}</span>
            <button type="button" className="cmd-link cmd-link--inline" onClick={onOpenTimeLogsThisWeek}>View Time Logs <CmdIcon name="arrow" className="cmd-link__arrow" /></button>
          </div>
          <DayBars days={labourThis.days} ariaLabel={`Actual hours by day: ${labourThis.days.map((d) => `${d.weekday} ${d.hours}`).join(", ")}`} />
          <ul className="cmd-legend cmd-legend--center">
            <li><i style={{ background: CHART_COLORS.blue }} />Actual Hours</li>
            <li className="is-muted" title="No planned-hours source exists yet"><i style={{ background: CHART_COLORS.gold, opacity: 0.35 }} />Planned Hours — not set</li>
          </ul>
        </>
      ) : (
        <Unavailable>{stateNote(data.timeLogs, "Time logs")}</Unavailable>
      )}
    </Widget>
  ) : null;

  const pipelineNode = allow.estimates ? (
    <Widget
      className="cmd-span-4"
      tone="gold"
      icon="funnel"
      title="Estimate Pipeline"
      subtitle={pipeline ? `Active Estimates (${pipeline.totalCount})` : "Active estimates"}
      exit={{ label: "View All", onClick: () => onOpenEstimateHubWithStatus?.(null) }}
      testId="cmd-pipeline"
    >
      {pipeline ? (
        <>
          <div className="cmd-pipeline">
            <Funnel stages={pipeline.stages} ariaLabel={`Estimate pipeline: ${pipeline.stages.map((s) => `${s.label} ${s.count}`).join(", ")}`} />
            <table className="cmd-table">
              <tbody>
                {pipeline.stages.map((stage, i) => (
                  <tr key={stage.key}>
                    <td><i style={{ background: FUNNEL_COLORS[i] }} />{stage.label}</td>
                    <td>{stage.count}</td>
                    <td>{formatMoneyCompact(stage.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cmd-total"><b>{formatMoneyCompact(pipeline.totalValue)}</b><span>Total Pipeline Value</span></div>
        </>
      ) : (
        <Unavailable>{stateNote(data.estimates, "Estimates")}</Unavailable>
      )}
    </Widget>
  ) : null;

  const planningNode = allow.tasks ? (
    <Widget className="cmd-span-4" tone="blue" icon="calendar" title="Planning – Next 7 Days" subtitle="Open tasks by due date" exit={{ label: "View Schedule", onClick: onOpenPlanning }} testId="cmd-planning">
      {planning ? (
        <ul className="cmd-days">
          {planning.days.map((day) => (
            <li key={day.iso} className={day.isToday ? "is-today" : ""}>
              <span className="cmd-days__day">{day.weekday}</span>
              <span className="cmd-days__date">{day.dateLabel}</span>
              <span className="cmd-days__count">{day.count} {day.count === 1 ? "task" : "tasks"}</span>
              <span className="cmd-days__bar"><span style={{ width: `${planning.max ? (day.count / planning.max) * 100 : 0}%` }} /></span>
            </li>
          ))}
        </ul>
      ) : (
        <Unavailable>{stateNote(data.tasks, "Planning")}</Unavailable>
      )}
    </Widget>
  ) : null;

  const financeNode = financeTiles.length > 0 ? (
    <Widget className="cmd-span-4" tone="gold" icon="wallet" title="Financial Position" subtitle="Current overview" exit={allow.finance ? { label: "View All", onClick: onOpenFinance } : allow.payables ? { label: "View All", onClick: onOpenAccountsPayable } : undefined} testId="cmd-finance">
      <div className="cmd-fin">
        {financeTiles.map((tile) => (
          <div key={tile.key} className={`cmd-fin__tile cmd-fin__tile--${tile.tone}`}>
            <span className="cmd-fin__icon"><CmdIcon name={tile.icon} /></span>
            <span className="cmd-fin__value">{tile.value}</span>
            <span className="cmd-fin__label">{tile.label}</span>
            {tile.sub ? <span className="cmd-fin__sub">{tile.sub}</span> : null}
          </div>
        ))}
      </div>
    </Widget>
  ) : null;

  const activityNode = (
  <Widget className="cmd-span-4" tone="gold" icon="pulse" title="Recent Activity" exit={{ label: "View All", onClick: onOpenTimeLogs }} testId="cmd-activity">
    {recentLogs.length ? (
      <ul className="cmd-feed">
        {recentLogs.slice(0, 5).map((log, i) => (
          <li
            key={`${log.timeLogId ?? log.dateIso}-${i}`}
            {...(log.timeLogId != null && typeof onOpenTimeLog === "function"
              ? {
                  className: "cmd-feed__item--link",
                  role: "button",
                  tabIndex: 0,
                  "data-time-log-id": String(log.timeLogId),
                  onClick: () => onOpenTimeLog(log.timeLogId),
                  onKeyDown: (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpenTimeLog(log.timeLogId);
                    }
                  },
                }
              : {})}
          >
            <span className="cmd-feed__icon"><CmdIcon name="clock" /></span>
            <span className="cmd-feed__text">
              <b>{log.workCompleted || "Time logged"}</b>
              <span>{[log.employeeName, log.projectName, `${log.hrs} hrs`].filter(Boolean).join(" · ")}</span>
            </span>
            {variant === "mobile" ? (
              (() => {
                const when = formatActivityWhen(log.dateIso, today, log.startTime);
                return (
                  <span className="cmd-feed__time cmd-feed__time--stack" data-testid="cmd-feed-when">
                    <b>{when.primary}</b>
                    {when.secondary ? <span>{when.secondary}</span> : null}
                  </span>
                );
              })()
            ) : (
              <span className="cmd-feed__time">{relativeDay(log.dateIso, today)}</span>
            )}
          </li>
        ))}
      </ul>
    ) : (
      <Unavailable>No recent time-log activity yet.</Unavailable>
    )}
  </Widget>
);

  const quickNode = quickActions.length > 0 ? (
    <section className="cmd-widget cmd-quick cmd-span-12 cmd-wide-m" data-testid="cmd-quick">
      <h2 className="cmd-quick__title"><span className="cmd-widget__icon"><CmdIcon name="bolt" /></span>Quick Access</h2>
      <div className="cmd-quick__row">
        {quickActions.map((action) => (
          <button key={action.id} type="button" className="cmd-quick__btn" onClick={action.onClick}>
            <span className={`cmd-quick__icon cmd-quick__icon--${action.tone || "gold"}`}><CmdIcon name={action.icon} /></span>
            {action.label}
          </button>
        ))}
      </div>
    </section>
  ) : null;

  const mobile = variant === "mobile";
  const preference = useMemo(
    () => loadHomeDashboardPreference(currentUser),
    [currentUser, layoutRevision],
  );
  const layout = resolveHomeDashboardLayout({
    variant: mobile ? "mobile" : "desktop",
    allow: { ...allow, quick: !mobile && quickActions.length > 0 },
    preference,
  });

  const commitLayout = (next) => {
    saveHomeDashboardPreference(currentUser, next);
    setLayoutRevision((value) => value + 1);
  };

  layoutRef.current = layout;
  commitRef.current = commitLayout;

  const beginSectionDrag = (id, event) => {
    if (event.button > 0) return;
    event.preventDefault();
    dragCleanup.current?.();
    setGrabbedId(id);
    const pointerId = event.pointerId;
    const startY = event.clientY;
    let insertAt = layoutRef.current.visible.indexOf(id);
    let moved = false;
    let lastSwitchY = startY;
    let pointerY = startY;
    let raf = 0;

    const measure = (clientY) => {
      const root = gridRef.current;
      if (!root) return insertAt;
      const boxes = [...root.querySelectorAll("[data-arrange-id]")].map((el) => {
        const rect = el.getBoundingClientRect();
        return {
          id: el.getAttribute("data-arrange-id"),
          top: rect.top,
          height: rect.height,
        };
      });
      return insertionIndex(clientY, boxes, id);
    };

    const applyIndex = (next, force) => {
      if (next === insertAt) return;
      if (!force && Math.abs(pointerY - lastSwitchY) < 28) return;
      lastSwitchY = pointerY;
      insertAt = next;
      setDrag({ id, insertAt: next });
    };

    const tick = () => {
      const edge = 72;
      let scrolled = false;
      if (pointerY < edge) {
        window.scrollBy(0, -18);
        scrolled = true;
      } else if (pointerY > window.innerHeight - edge) {
        window.scrollBy(0, 18);
        scrolled = true;
      }
      if (moved) applyIndex(measure(pointerY), scrolled);
      raf = window.requestAnimationFrame(tick);
    };

    const onMove = (ev) => {
      if (ev.pointerId !== pointerId) return;
      pointerY = ev.clientY;
      if (!moved && Math.abs(ev.clientY - startY) < 6) return;
      if (!moved) {
        moved = true;
        setDrag({ id, insertAt });
        raf = window.requestAnimationFrame(tick);
      }
      applyIndex(measure(ev.clientY), false);
    };

    const finish = (ev) => {
      if (ev && ev.pointerId != null && ev.pointerId !== pointerId) return;
      window.cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      window.removeEventListener("keydown", onKey);
      dragCleanup.current = null;
      const fromOrder = layoutRef.current.visible;
      const placed = previewHomeOrder(fromOrder, id, insertAt);
      setGrabbedId(null);
      setDrag(null);
      if (moved && placed.some((item, index) => item !== fromOrder[index])) {
        commitRef.current(placeHomeWidget(layoutRef.current, id, insertAt));
      }
    };

    const onKey = (ev) => {
      if (ev.key !== "Escape") return;
      moved = false;
      finish({ pointerId });
    };

    dragCleanup.current = () => finish({ pointerId });
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
    window.addEventListener("keydown", onKey);
  };

  const nodes = {
    needs: needsNode,
    today: todayNode,
    health: healthNode,
    taskov: taskovNode,
    planning: planningNode,
    cash: cashNode,
    labour: labourNode,
    pipeline: pipelineNode,
    finance: financeNode,
    activity: activityNode,
    quick: quickNode,
  };

  const shownIds = drag
    ? previewHomeOrder(layout.visible, drag.id, drag.insertAt)
    : layout.visible;

  const renderSection = (id) => {
    const node = nodes[id];
    if (!node) return null;
    if (!arranging) return node;
    const controls = arrangeControls(id);
    const dragging = drag?.id === id;
    const grabbed = grabbedId === id;
    if (id === "quick") {
      return React.cloneElement(
        node,
        {
          className: `${node.props.className || ""} is-arranging${grabbed ? " is-grabbed" : ""}${dragging ? " is-dragging" : ""}`.trim(),
          "data-arrange-id": id,
          "data-grabbed": grabbed ? "true" : undefined,
        },
        controls,
        ...React.Children.toArray(node.props.children),
      );
    }
    return React.cloneElement(node, { arrange: controls, arrangeId: id, dragging, grabbed });
  };

  const arrangeControls = (id) => {
    if (!arranging) return null;
    const label = HOME_WIDGETS[id]?.label || "widget";
    const index = shownIds.indexOf(id);
    return (
      <div className="cmd-widget__arrange" data-testid={`cmd-arrange-${id}`}>
        <button
          type="button"
          className="cmd-arrange-handle"
          aria-label={`Drag ${label}`}
          aria-pressed={grabbedId === id}
          data-testid={`cmd-drag-${id}`}
          onPointerDown={(event) => beginSectionDrag(id, event)}
          onKeyDown={(event) => {
            if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
            event.preventDefault();
            commitLayout(moveHomeWidget(layout, id, event.key === "ArrowUp" ? "up" : "down"));
          }}
        >
          <span className="cmd-arrange-handle__bars" aria-hidden="true" />
        </button>
        <button type="button" className="cmd-arrange-btn cmd-arrange-btn--step" aria-label={`Move ${label} up`} disabled={index <= 0} onClick={() => commitLayout(moveHomeWidget(layout, id, "up"))}>Up</button>
        <button type="button" className="cmd-arrange-btn cmd-arrange-btn--step" aria-label={`Move ${label} down`} disabled={index < 0 || index >= shownIds.length - 1} onClick={() => commitLayout(moveHomeWidget(layout, id, "down"))}>Down</button>
        <button type="button" className="cmd-arrange-btn cmd-arrange-btn--hide" aria-label={`Hide ${label}`} onClick={() => commitLayout(hideHomeWidget(layout, id))}>Hide</button>
      </div>
    );
  };

  const sessionState = !activeSession ? "idle" : activeSession.break_active ? "break" : "work";
  const sessionClass = sessionState === "break" ? "cmd-cta is-break" : sessionState === "work" ? "cmd-cta is-live" : "cmd-cta";

  return (
    <div className={`cmd-dash${mobile ? " cmd-dash--mobile" : ""}`} data-testid={mobile ? "mobile-command-dashboard" : "desktop-command-dashboard"}>
      <header className="cmd-header">
        {mobile ? null : <button type="button" className="cmd-brand" onClick={onOpenAshleyHome} aria-label="Open Ashley Home">
          <img src="/logo512.png" alt="" className="cmd-brand__mark" draggable={false} />
          <span className="cmd-brand__text">
            <span className="cmd-brand__name">CrewPilot</span>
            <span className="cmd-brand__tag">AI-enhanced work management</span>
          </span>
        </button>}
        <div className="cmd-greeting">
          <CmdIcon name={greeting.icon} className="cmd-greeting__icon" />
          <div>
            <h1 className="cmd-greeting__title">{greeting.text}, {firstName}</h1>
            <p className="cmd-greeting__sub"><span>{dateLabel}</span><span className="cmd-greeting__sep" /> Here’s what’s happening with your business today.</p>
          </div>
        </div>
        <div className="cmd-header__actions">
          {!mobile && <button type="button" className="cmd-icon-btn" aria-label="Guide" title="Guide" onClick={onOpenGuide}><CmdIcon name="help" /></button>}
          {onOpenUserPreferences && !mobile ? (
            <button type="button" className="cmd-avatar" aria-label="Settings" title="Settings" onClick={onOpenUserPreferences}>{initials(userName)}</button>
          ) : null}
          {arranging ? null : (
            <button type="button" className="cmd-arrange-entry" data-testid="cmd-arrange" onClick={() => setArranging(true)}>Arrange</button>
          )}
          <button type="button" className={sessionClass} data-session-state={sessionState} onClick={() => onOpenSession?.()}>
            <CmdIcon name={activeSession ? "clock" : "plus"} />
            {activeSession ? (activeSession.break_active ? "On Break — Continue" : "Continue Work Session") : "Start Work Session"}
          </button>
        </div>
      </header>

      {arranging ? (
        <div className="cmd-arrange-bar" data-testid="cmd-arrange-bar" role="region" aria-label="Arrange dashboard">
          <p className="cmd-arrange-bar__label">Drag a section by its handle. Arrow buttons remain for keyboard.</p>
          <div className="cmd-arrange-slot">
            <button type="button" className="cmd-arrange-btn" data-testid="cmd-arrange-reset" onClick={() => { clearHomeDashboardPreference(currentUser); setLayoutRevision((value) => value + 1); }}>Reset to Default</button>
            <button type="button" className="cmd-arrange-btn cmd-arrange-btn--done" data-testid="cmd-arrange-done" onClick={() => setArranging(false)}>Done</button>
          </div>
        </div>
      ) : null}

      <div ref={gridRef} className={`cmd-grid${arranging ? " is-arranging" : ""}${drag ? " is-dragging" : ""}`} data-testid="cmd-widget-order" data-order={shownIds.join(" ")}>
        {shownIds.map((id) => {
          const node = renderSection(id);
          if (!node) return null;
          return <React.Fragment key={id}>{node}</React.Fragment>;
        })}
      </div>

      {arranging ? (
        <section className="cmd-hidden" data-testid="cmd-hidden-widgets" aria-label="Hidden widgets">
          <h2 className="cmd-hidden__title">Hidden</h2>
          {layout.hidden.length === 0 ? (
            <p className="cmd-hidden__empty">No hidden widgets.</p>
          ) : (
            <ul className="cmd-hidden__list">
              {layout.hidden.map((id) => (
                <li key={id}>
                  <span>{HOME_WIDGETS[id]?.label || id}</span>
                  <button type="button" className="cmd-arrange-btn" data-testid={`cmd-show-${id}`} onClick={() => commitLayout(showHomeWidget(layout, id))}>Show</button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}
